import * as React from 'react';
import { IndexRoute, Redirect, Route } from 'react-router';

import { WidgetItemDynamic } from '@coursera-web/item-widget';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import LegacyDataFetch from 'bundles/course-v2/components/LegacyDataFetch';
import { ProgrammingAssignmentsSubViews } from 'bundles/item-programming/types';
import { ASSIGNMENT_SUBMISSION_BASE_PATH, LECTURE_BASE_PATH } from 'bundles/item/constants/constants';

const ItemPage = loadOnRoute(() => import('bundles/item/components/ItemPage'));

const WiseFlowItem = loadOnRoute(() => import('bundles/lms-interop/wise-flow-item/WiseFlowItem'));
const LtiItem = loadOnRoute(() => import('bundles/item-lti/components/LtiItem'));
const UngradedLabItem = loadOnRoute(() => import('bundles/item-ungraded-lab/components/UngradedLabItem'));
const LearnerCoachItemContainer = loadOnRoute(
  () => import('bundles/coach-item/learner-coach-item/components/LearnerCoachItemContainer')
);
const DiscussionPromptWrapper = loadOnRoute(() => import('bundles/item/components/DiscussionPromptWrapper'));
const GradedDiscussionPromptItem = loadOnRoute(
  () => import('bundles/graded-discussion-prompt/components/GradedDiscussionPromptItem')
);

const PlaceholderItem = loadOnRoute(() => import('bundles/item/components/PlaceholderItem'));

const VideoItemContainer = loadOnRoute(() => import('bundles/item-lecture/components/cds/VideoItemContainer'));
const VideoItemFetcher = loadOnRoute(() => import('bundles/item-lecture/components/cds/VideoItemFetcher'));

const WidgetItem = loadOnRoute(WidgetItemDynamic);
const ReadingItem = loadOnRoute(() => import('bundles/item-reading/components/ReadingItem'));

const ProgrammingItem = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingItem'));
const ProgrammingSubmission = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingSubmission'));
const ProgrammingDiscussions = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingDiscussions'));
const ProgrammingInstructions = loadOnRoute(
  () => import('bundles/item-programming/components/ProgrammingInstructions')
);
const ProgrammingSharing = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingSharing'));
const LaunchedLabLearner = loadOnRoute(() => import('bundles/launched-lab/components/LaunchedLabLearner'));

const PeerItemPage = loadOnRoute(() => import('bundles/peer/components/page/PeerItemPage'));
const PeerItemGiveFeedback = loadOnRoute(() => import('bundles/peer/components/PeerItemGiveFeedback'));
const PeerItemInstructions = loadOnRoute(() => import('bundles/peer/components/PeerItemInstructions'));
const PeerItemReview = loadOnRoute(() => import('bundles/peer/components/PeerItemReview'));
const PeerItemReviewNext = loadOnRoute(() => import('bundles/peer/components/PeerItemReviewNext'));
const PeerItemRubricPreview = loadOnRoute(() => import('bundles/peer/components/rubric-preview/PeerItemRubricPreview'));
const PeerItemSubmit = loadOnRoute(() => import('bundles/peer/components/PeerItemSubmit'));

const PeerItemDiscussionsContainer = loadOnRoute(() => import('bundles/peer/components/PeerItemDiscussionsContainer'));

const TeammateReviewItem = loadOnRoute(() => import('bundles/teammate-review/components/cds/TeammateReviewItem'));
const TeammateReviewCover = loadOnRoute(
  () => import('bundles/teammate-review/components/cover/cds/TeammateReviewCover')
);
const TeammateReview = loadOnRoute(() => import('bundles/teammate-review/components/review/cds/TeammateReview'));
const TeammateReviewGrades = loadOnRoute(() => import('bundles/teammate-review/components/grades/cds/TMRGrades'));

const QuizCoverPage = loadOnRoute(() => import('bundles/quiz-compound-assessment/components/cds/QuizCoverPage'));

const PracticeQuizCoverPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/PracticeQuizCoverPage')
);
const QuizAttemptPage = loadOnRoute(() => import('bundles/quiz-compound-assessment/components/cds/AttemptPage'));

const PracticeQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/PracticeQuizAttemptPage')
);

const SubmittedQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/SubmittedAttemptPage')
);

const SubmittedPracticeQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/SubmittedPracticeQuizAttemptPage')
);

const AssignmentViewWrapper = loadOnRoute(
  () => import('bundles/assignment-submission/components/assignment-view/AssignmentViewWrapper')
);

const AssignmentSubmissionCover = loadOnRoute(
  () => import('bundles/assignment-submission/components/cover/CoverRoute')
);

// We now prefix all questions with the path "threads" so add a redirect from old routes.
const generateDiscussionsThreadsRedirect = () => {
  return (
    <Route>
      <Redirect from=":question_id" to="threads/:question_id" />
      <Redirect from=":question_id/replies/:answer_id" to="threads/:question_id/replies/:answer_id" />
      <Redirect
        from=":question_id/replies/:answer_id/comments/:comment_id"
        to="threads/:question_id/replies/:answer_id/comments/:comment_id"
      />
    </Route>
  );
};

export default [
  // routes at top to get component in blank page and avoid ItemPage layout
  <Route key="csrItemRoutes" component={LegacyDataFetch}>
    <Route path="programming/:itemId(/:slug)/lab" name="programmingLab" getComponent={LaunchedLabLearner} />
    <Route path="ungradedLab/:itemId(/:slug)/lab" name="programmingLab" getComponent={LaunchedLabLearner} />
    <Route getComponent={ItemPage}>
      <Route path="peer/:item_id/" name="peer" getComponent={PeerItemPage}>
        <Route path=":slug">
          <IndexRoute name="peer.instructions" getComponent={PeerItemInstructions} />
          <Route path="submit" name="peer.submit" getComponent={PeerItemSubmit} />
          <Route path="rubric-preview" name="peer.rubricPreview" getComponent={PeerItemRubricPreview} />
          <Route path="give-feedback" name="peer.give-feedback" getComponent={PeerItemGiveFeedback} />
          <Route path="review-next" name="peer.review-next" getComponent={PeerItemReviewNext} />
          <Route path="review/:submission_id(/admin)" name="peer.review" getComponent={PeerItemReview} />

          <Route path="discussions" name="peer.discussions">
            <IndexRoute getComponent={PeerItemDiscussionsContainer} />
            <Route path="threads/:question_id" getComponent={PeerItemDiscussionsContainer} />
            <Route path="threads/:question_id/replies/:answer_id" getComponent={PeerItemDiscussionsContainer} />
            <Route
              path="threads/:question_id/replies/:answer_id/comments/:comment_id"
              getComponent={PeerItemDiscussionsContainer}
            />

            {generateDiscussionsThreadsRedirect()}
          </Route>
        </Route>
      </Route>

      <Route path="discussionPrompt/:item_id(/:slug)" name="discussionPrompt" getComponent={DiscussionPromptWrapper} />

      <Route
        path="gradedDiscussionPrompt/:item_id(/:slug)"
        name="gradedDiscussionPrompt"
        getComponent={GradedDiscussionPromptItem}
      />
      <Route path="ungradedWidget/:item_id(/:slug)" name="widget" getComponent={WidgetItem} />
      <Route path="gradedWidget/:item_id(/:slug)" name="gradedWidget" getComponent={WidgetItem} />
      <Route path="supplement/:item_id(/:slug)" name="reading" getComponent={ReadingItem} />
      <Route path="wiseFlow/:item_id(/:slug)" name="wiseFlow" getComponent={WiseFlowItem} />
      <Route path="gradedLti/:item_id(/:slug)" name="gradedLti" getComponent={LtiItem} />
      <Route path="ungradedLti/:item_id(/:slug)" name="ungradedLti" getComponent={LtiItem} />
      <Route path="ungradedLab/:item_id(/:slug)" name="ungradedLab" getComponent={UngradedLabItem} />
      <Route path="coach/:item_id(/:slug)" name="coach" getComponent={LearnerCoachItemContainer} />
      <Route path="programming/:item_id(/:slug)" name="programming" getComponent={ProgrammingItem}>
        <IndexRoute name={ProgrammingAssignmentsSubViews.DEFAULT} getComponent={ProgrammingInstructions} />
        <Route
          path={ProgrammingAssignmentsSubViews.INSTRUCTIONS}
          name={ProgrammingAssignmentsSubViews.INSTRUCTIONS}
          getComponent={ProgrammingInstructions}
        />
        <Route
          path={ProgrammingAssignmentsSubViews.SUBMISSIONS}
          name={ProgrammingAssignmentsSubViews.SUBMISSIONS}
          getComponent={ProgrammingSubmission}
        />
        <Route path="discussions" name={ProgrammingAssignmentsSubViews.DISCUSSIONS}>
          <IndexRoute getComponent={ProgrammingDiscussions} />
          <Route path="threads/:question_id" getComponent={ProgrammingDiscussions} />
          <Route path="threads/:question_id/replies/:answer_id" getComponent={ProgrammingDiscussions} />
          <Route
            path="threads/:question_id/replies/:answer_id/comments/:comment_id"
            getComponent={ProgrammingDiscussions}
          />

          {generateDiscussionsThreadsRedirect()}
        </Route>
        <Route
          path={ProgrammingAssignmentsSubViews.SHARING}
          name={ProgrammingAssignmentsSubViews.SHARING}
          getComponent={ProgrammingSharing}
        />
      </Route>
      <Route />

      {/* On course week page links to items are provided by BE. TMR items have wrong */}
      {/* path: `teammateReview` instead of `teammate-review`. This is a temporary */}
      {/* redirect until we will fix it on BE. */}
      <Redirect from="teammateReview/:item_id(/:slug)" to="teammate-review/:item_id(/:slug)" />

      <Route path="teammate-review/:item_id(/:slug)" name="teammate-review-item" getComponent={TeammateReviewItem}>
        <IndexRoute name="teammate-review-cover" getComponent={TeammateReviewCover} />
        <Route path="review/:teammateId" name="teammate-review-review" getComponent={TeammateReview} />
        <Route path="grades" name="teammate-review-grades" getComponent={TeammateReviewGrades} />
      </Route>

      <Redirect from="team/:item_id" to={ASSIGNMENT_SUBMISSION_BASE_PATH + '/:item_id'} />
      <Redirect from="team/:item_id/:slug(/:extra)" to={ASSIGNMENT_SUBMISSION_BASE_PATH + '/:item_id/:slug'} />
      {/* Redirect ungraded assignment route from Consumer Home page to assignment-submission because the original URL comes from the BE */}
      <Redirect from="ungradedAssignment/:item_id" to={ASSIGNMENT_SUBMISSION_BASE_PATH + '/:item_id'} />
      <Redirect
        from="ungradedAssignment/:item_id/:slug(/:extra)"
        to={ASSIGNMENT_SUBMISSION_BASE_PATH + '/:item_id/:slug'}
      />

      <Route
        path={ASSIGNMENT_SUBMISSION_BASE_PATH + '/:item_id(/:slug)'}
        name="assignment-submission-cover"
        getComponent={AssignmentSubmissionCover}
      >
        <Route path=":viewType" name="assignment-submission-view" getComponent={AssignmentViewWrapper} />
      </Route>

      <Route path="exam/:item_id(/:slug)" name="quiz-cover" getComponent={QuizCoverPage}>
        <Route path="attempt" name="quiz-attempt" getComponent={QuizAttemptPage} />
        <Route path="view-attempt" name="quiz-view-attempt" getComponent={SubmittedQuizAttemptPage} />
      </Route>
      <Route path="quiz/:item_id(/:slug)" name="practice-quiz-cover" getComponent={PracticeQuizCoverPage}>
        <Route path="attempt" name="practice-quiz-attempt" getComponent={PracticeQuizAttemptPage} />
        <Route path="view-attempt" name="practice-quiz-view-attempt" getComponent={SubmittedPracticeQuizAttemptPage} />
      </Route>

      <Route path="placeholder/:item_id(/:slug)" name="placeholder" getComponent={PlaceholderItem} />
    </Route>
  </Route>,
  <Route key="videoItem" path={LECTURE_BASE_PATH + '/:item_id(/:slug)'} getComponent={VideoItemFetcher}>
    <Route component={LegacyDataFetch}>
      <Route getComponent={ItemPage}>
        <IndexRoute name="lecture" getComponent={VideoItemContainer} />
      </Route>
    </Route>
  </Route>,
];
